import { useState, useEffect, createContext, useContext } from 'react'

const LOCAL_STORAGE_KEY = '@based-ui-theme'
const DEFAULT_THEME: Theme = 'light'
const ATTRIBUTE_NAME = 'data-theme'

// This script is necessary because we want to set the data-theme attribute before the first render
// so that light/dark does not flash
// It should be placed somewhere in the body of the document, since it's sync it will stop the document parsing and so a render won't happen which is exactly what we are looking for
export const THEME_PREVENT_FLASH_SCRIPT_SRC = `
  <script>
    !function(){
      try {
        var t = localStorage.getItem("${LOCAL_STORAGE_KEY}");
        document.documentElement.setAttribute("${ATTRIBUTE_NAME}", t ?? "${DEFAULT_THEME}");
      } catch(e) {}
    }();
  </script>
`

type Theme = 'auto' | 'dark' | 'light'

type UseThemeProps = {
  theme: Theme
  setTheme: React.Dispatch<React.SetStateAction<Theme>>
  toggleTheme: () => void
  getSystemTheme: () => 'dark' | 'light'
}

export const getSystemTheme = () =>
  window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

const defaultContext: UseThemeProps = {
  theme: DEFAULT_THEME,
  setTheme: () => {},
  toggleTheme: () => {},
  getSystemTheme,
}

const ThemeContext = createContext<UseThemeProps>(defaultContext)

export const useTheme = () => {
  return useContext(ThemeContext)
}

export type ThemeProviderProps = {
  children: React.ReactNode
  forcedTheme?: Theme
}

export const ThemeProvider = ({
  children,
  forcedTheme,
}: ThemeProviderProps) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const fromAttr = document.documentElement.getAttribute(
      ATTRIBUTE_NAME,
    ) as Theme | null

    if (fromAttr) return fromAttr

    try {
      const fromStorage = localStorage.getItem(
        LOCAL_STORAGE_KEY,
      ) as Theme | null
      if (fromStorage) return fromStorage
    } catch {}

    return DEFAULT_THEME
  })

  useEffect(() => {
    let newVal = theme

    if (newVal === 'auto') {
      newVal = getSystemTheme()
    }

    document.documentElement.setAttribute(ATTRIBUTE_NAME, newVal)

    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, newVal)
    } catch {}
  }, [theme])

  useEffect(() => {
    if (!forcedTheme) return
    if (forcedTheme === 'auto') {
      setTheme(getSystemTheme())
    } else {
      setTheme(forcedTheme)
    }
  }, [forcedTheme])

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === ATTRIBUTE_NAME) {
          const newVal = document.documentElement.getAttribute(
            ATTRIBUTE_NAME,
          ) as Theme

          if (newVal && newVal !== theme) {
            setTheme(newVal)
          }
        }
      }
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: [ATTRIBUTE_NAME],
    })
    return () => observer.disconnect()
  }, [theme])

  const toggleTheme = () => {
    const autoToggle = getSystemTheme() === 'dark' ? 'light' : 'dark'

    const next = {
      dark: 'light',
      light: 'dark',
      auto: autoToggle,
    }[theme] as Theme

    setTheme(next)
  }

  return (
    <ThemeContext.Provider
      value={{ theme, setTheme, toggleTheme, getSystemTheme }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
